import {
  Component,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormGroup,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ERROR_MESSAGE } from 'src/app/_helpers/utils';
import { FormBase } from 'src/app/_models/form-base';
import { MatIcon } from '@angular/material/icon';
import { MatError } from '@angular/material/form-field';

@Component({
    selector: 'app-number-form-field',
    imports: [FormsModule, ReactiveFormsModule, MatIcon, MatError],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './number-form-field.component.html',
    styleUrl: './number-form-field.component.scss'
})
export class NumberFormFieldComponent {
  /** Field configuration object (required) */
  field = input<FormBase<number>>();

  /** Parent FormGroup containing the control (required)  */
  parentForm = input<FormGroup>();

  /** Minimum allowed value for number inputs (optional) */
  minValue = input<number | undefined>(undefined);

  /** Maximum allowed value for number inputs (optional) */
  maxValue = input<number | undefined>(undefined);

  /** Step value for number inputs (optional) */
  step = input<number | undefined>(undefined);

  /** Determines if a tooltip should be displayed (default: `true`) */
  displayTooltip = input<boolean>(true);

  /** Text to display in the tooltip (optional) */
  tooltipText = input<string | undefined>(undefined);

  /** Emits when the minus button is clicked */
  minusButtonClick = output<void>();

  /** Emits when the plus button is clicked */
  plusButtonClick = output<void>();

  /** Determines if the label should be displayed above the input (default: `false`) */
  labelAbove = input<boolean>(false);

  /**
   * Indicates whether the password field is in visible mode.
   */
  showing = false;

  /** Warning message displayed when validation fails (optional) */
  warningMessage = input<string | undefined>(undefined);

  /** Controls whether the warning message should be shown (default: `false`) */
  showWarning = input<boolean>(false);

  /**
   * Retrieves the `FormControl` associated with this form field.
   * @returns The `FormControl` instance from the parent form.
   */
  public get control(): FormControl | null {
    const form = this.parentForm();
    const f = this.field();
    if (!form || !f) return null;
    return form.controls[f.key] as FormControl;
  }

  /**
   * Determines whether the field has validation errors.
   * @returns `true` if the field is invalid and has been touched, otherwise `false`.
   */
  public get hasErrors(): boolean {
    const c = this.control;
    return !!c && c.invalid && c.touched;
  }

  /**
   * Retrieves a list of validation error messages for the field.
   * @returns An array of error messages corresponding to validation failures.
   */
  public get errorsList(): string[] {
    if (!this.hasErrors) return [];
    const c = this.control;
    if (!c) return [];
    return Object.keys(c.errors || {}).map((err) => {
      const errorFn = ERROR_MESSAGE[err as keyof typeof ERROR_MESSAGE];
      return errorFn ? errorFn(c.getError(err) as never) : 'Unknown error';
    });
  }

  /**
   * Retrieves the text for toggling password visibility.
   * @returns `'Hide'` if password is currently visible, otherwise `'Show'`.
   */
  public get showHideText(): string {
    return this.showing ? 'Hide' : 'Show';
  }

  /**
   * Emits the `minusButtonClick` event (used for decrementing number fields).
   */
  minusButtonClicked(): void {
    const c = this.control;
    if (!c) return;
    const currentValue = c.value as number;

    // Ensure we have a valid number
    if (typeof currentValue !== 'number' || isNaN(currentValue)) return;

    const step = this.step() ?? 1;
    const minValue = this.minValue() ?? -Infinity;

    // Calculate decimal places from step to avoid floating point precision issues
    const decimalPlaces = this.getDecimalPlaces(step);
    const newValue = this.roundToDecimalPlaces(currentValue - step, decimalPlaces);

    if (newValue >= minValue) {
      c.setValue(newValue);
      c.markAsDirty();
      const form = this.parentForm();
      if (form) form.markAsDirty();
    }

    // emit via output signal
    this.minusButtonClick.emit?.();
  }

  /**
   * Emits the `plusButtonClick` event (used for incrementing number fields).
   */
  plusButtonClicked(): void {
    const c = this.control;
    if (!c) return;
    const currentValue = c.value as number;

    // Ensure we have a valid number
    if (typeof currentValue !== 'number' || isNaN(currentValue)) return;

    const step = this.step() ?? 1;
    const maxValue = this.maxValue() ?? Infinity;

    // Calculate decimal places from step to avoid floating point precision issues
    const decimalPlaces = this.getDecimalPlaces(step);
    const newValue = this.roundToDecimalPlaces(currentValue + step, decimalPlaces);

    if (newValue <= maxValue) {
      c.setValue(newValue);
      c.markAsDirty();
      const form = this.parentForm();
      if (form) form.markAsDirty();
    }

    // emit via output signal
    this.plusButtonClick.emit?.();
  }

  /**
   * Helper method to determine the number of decimal places in a number
   */
  private getDecimalPlaces(num: number): number {
    if (Math.floor(num) === num) return 0;
    const str = num.toString();
    if (str.indexOf('.') !== -1 && str.indexOf('e-') === -1) {
      return str.split('.')[1].length;
    } else if (str.indexOf('e-') !== -1) {
      const parts = str.split('e-');
      return parseInt(parts[1], 10);
    }
    return 0;
  }

  /**
   * Helper method to round a number to a specific number of decimal places
   */
  private roundToDecimalPlaces(num: number, decimalPlaces: number): number {
    return (
      Math.round(num * Math.pow(10, decimalPlaces)) /
      Math.pow(10, decimalPlaces)
    );
  }
}
